import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  hangmanEngine,
  toView,
  type HangmanAction,
  type HangmanState,
  type HangmanView,
  type ReadingBand,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO_PLAYER = 'solo'

const PLAYER = { id: SOLO_PLAYER, displayName: 'You', userId: null } as const

interface Game {
  readonly seed: string
  readonly state: HangmanState
  /** Only the moves the engine accepted, so a server replay follows the same path. */
  readonly actions: readonly HangmanAction[]
  readonly startedAt: number
}

type GameEvent = { type: 'play'; action: HangmanAction; now: number }

function start(seed: string, band: ReadingBand, now: number): Game {
  return {
    seed,
    state: hangmanEngine.createInitialState({ band }, [PLAYER], createRng(seed)),
    actions: [],
    startedAt: now,
  }
}

/**
 * A reducer rather than setState calls.
 *
 * React can invoke a state updater more than once, so doing anything with a
 * side effect inside one — recording a finished game, say — can happen twice.
 * Keeping this pure and firing the submission from an effect means a game is
 * recorded exactly once, whatever React decides to re-run.
 */
function reduceGame(game: Game, event: GameEvent): Game {
  const outcome = applyAction(hangmanEngine, game.state, event.action, {
    playerId: SOLO_PLAYER,
    now: event.now,
  })

  if (!outcome.ok) return game

  return { ...game, state: outcome.state, actions: [...game.actions, event.action] }
}

export interface Hangman {
  readonly view: HangmanView
  readonly guess: (letter: string) => void
  readonly guessWord: (word: string) => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
}

/**
 * A word from a round the server dealt.
 *
 * The seed is the server's, and the finished game is recorded against the round
 * it names — so a game cannot be recorded twice, and its mistakes cannot be
 * left out of the telling.
 */
export function useHangman(
  round: DealtRound<{ band: ReadingBand }>,
  onFinished: () => void,
): Hangman {
  const band = round.config.band

  const [game, dispatch] = useReducer(reduceGame, round.seed, (seed) =>
    start(seed, band, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = game.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    // Everything she did reaches the server before it is asked to score it.
    // The submission carries nothing else: the moves are already there.
    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The round
        // is not spent, so it can still be finished from another device — and
        // asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She finished the game
      // either way, and a network blip is not her problem.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  const play = useCallback(
    (action: HangmanAction) => {
      const now = Date.now()
      dispatch({ type: 'play', action, now })
      moves.record(action, Math.max(0, now - game.startedAt))
    },
    [moves, game.startedAt],
  )

  return {
    view: useMemo(() => toView(game.state), [game.state]),
    guess: useCallback((letter: string) => play({ type: 'guessLetter', letter }), [play]),
    guessWord: useCallback((word: string) => play({ type: 'guessWord', word }), [play]),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
